import React, { useState } from 'react';
import { Table, Button, message, App } from 'antd';
import { DeleteOutlined, PlusOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { FieldValues } from 'react-hook-form';
import * as Yup from 'yup';
import { BrandCustomData as BrandCustomDataType } from '../../../../types/brand_settings';
import PageHookForm, { CustomHookFormActions } from '../../global/page/PageHookForm';
import FormInput from '../../global/Forms/FormInput';

type BrandCustomDataProps = {
  data: BrandCustomDataType[];
  loading: boolean;
  onSave: (customData: BrandCustomDataType[]) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
};

type EditableRecord = BrandCustomDataType & {
  isNew?: boolean;
  tempId?: string;
};

const BrandCustomData: React.FC<BrandCustomDataProps> = ({ data, loading, onSave, onDelete }) => {
  const { t } = useTranslation();
  const { modal } = App.useApp();
  const [deletingRecords, setDeletingRecords] = useState<Set<number | string>>(new Set());

  const initialValues = React.useMemo(
    () => ({ customData: data.map(item => ({ ...item })) }),
    [data]
  );

  const validationSchema = Yup.object().shape({
    customData: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().trim().required(t('settings:brandCustomData.nameRequired')),
        value: Yup.string().trim().required(t('settings:brandCustomData.valueRequired')),
      })
    ),
  });

  const handleSubmit = async (values: FieldValues, actions: CustomHookFormActions) => {
    const { setSubmitPending, setSubmitSuccess, setSubmitError } = actions;

    setSubmitPending();
    try {
      const dataToSave: BrandCustomDataType[] = values.customData.map((item: EditableRecord) => {
        const { tempId, isNew, ...rest } = item;
        if (isNew) {
          const { id, ...newRecord } = rest;
          return newRecord;
        }
        return rest;
      });

      await onSave(dataToSave);
      message.success(t('settings:brandCustomData.saveSuccess'));
      setSubmitSuccess();
    } catch (error) {
      message.error(t('settings:brandCustomData.saveFailed'));
      setSubmitError(t('settings:brandCustomData.saveFailed'));
    }
  };

  const renderFormContent = (formProps: any) => {
    const { setValue, watch } = formProps;
    const currentValues = watch();

    const handleAddNew = () => {
      const tempId = `new-${Date.now()}`;
      const newRecord: EditableRecord = {
        name: '',
        value: '',
        isNew: true,
        tempId,
      };
      setValue('customData', [...currentValues.customData, newRecord], { shouldDirty: true });
    };

    const handleDelete = (record: EditableRecord, index: number) => {
      if (record.isNew) {
        const updatedData = currentValues.customData.filter(
          (_item: EditableRecord, idx: number) => idx !== index
        );
        setValue('customData', updatedData, { shouldDirty: true });
      } else if (record.id) {
        modal.confirm({
          title: t('settings:brandCustomData.confirmDelete'),
          icon: <ExclamationCircleOutlined />,
          okText: t('common:delete'),
          cancelText: t('common:cancel'),
          okButtonProps: { danger: true },
          onOk: async () => {
            setDeletingRecords(prev => new Set(prev).add(record.id!));
            try {
              await onDelete(record.id!);
              message.success(t('settings:brandCustomData.deleteSuccess'));
            } catch (error) {
              message.error(t('settings:brandCustomData.saveFailed'));
            } finally {
              setDeletingRecords(prev => {
                const newSet = new Set(prev);
                newSet.delete(record.id!);
                return newSet;
              });
            }
          },
        });
      }
    };

    const getRecordKey = (record: EditableRecord) => {
      return record.tempId || record.id || '';
    };

    const columns = [
      {
        title: t('settings:brandCustomData.fieldName'),
        dataIndex: 'name',
        key: 'name',
        width: '40%',
        render: (text: string, record: EditableRecord, index: number) => {
          return (
            <FormInput
              name={`customData.${index}.name`}
              placeholder={t('settings:brandCustomData.fieldName')}
              disabled={!record.isNew && !!record.id}
              useHookForm
            />
          );
        },
      },
      {
        title: t('settings:brandCustomData.fieldValue'),
        dataIndex: 'value',
        key: 'value',
        width: '40%',
        render: (text: string, record: EditableRecord, index: number) => {
          return (
            <FormInput
              name={`customData.${index}.value`}
              placeholder={t('settings:brandCustomData.fieldValue')}
              useHookForm
            />
          );
        },
      },
      {
        title: t('settings:brandCustomData.actions'),
        key: 'actions',
        width: '20%',
        render: (_: any, record: EditableRecord, index: number) => {
          const isDeleting = record.id != null && deletingRecords.has(record.id);
          return (
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record, index)}
              loading={isDeleting}
            >
              {t('settings:brandCustomData.delete')}
            </Button>
          );
        },
      },
    ];

    return (
      <div className="content settings-default">
        <Table
          dataSource={currentValues.customData}
          columns={columns}
          rowKey={getRecordKey}
          loading={loading}
          pagination={false}
          locale={{
            emptyText: t('settings:brandCustomData.noData'),
          }}
        />

        <div className="brand-custom-data__add-button-container">
          <Button type="default" icon={<PlusOutlined />} onClick={handleAddNew}>
            {t('settings:brandCustomData.createNewRecord')}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <PageHookForm
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      contentNoSpacing
      enableReinitialize
    >
      {formProps => renderFormContent(formProps)}
    </PageHookForm>
  );
};

export default BrandCustomData;
